import { Component, HostListener, Input, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';
import { FeedbackView, SecretaryJudgeValues } from '../feedback.service';
import { categoryResultsUrl, eventPageUrl } from '../feedback-links';
import { DecimalPipe } from '@angular/common';

type JudgeFeedback = {
  judgeCode: string;
  feedback: string | null;
  judgeValues: SecretaryJudgeValues | null;
};

type TableType = 'TVS' | 'AVBS';

type Table = {
  tableType: TableType;
  performanceId: number;
  url: SafeResourceUrl;
  height: number;
};

type Section = {
  kind: 'table' | 'judges';
  label: string;
  collapsed: boolean;
  showScores: boolean;
  table: Table | null;
  feedbacks: JudgeFeedback[];
};

type Program = {
  sections: Section[];
  collapsed: boolean;
};

type Round = {
  roundName: string;
  programs: Program[];
};

const INITIAL_TABLE_HEIGHT = 200;

const SUBPANEL_ORDER = ['TV', 'AV', 'AV-A', 'AV-B', 'EXE'];

@Component({
  selector: 'agg-feedback-view',
  standalone: true,
  imports: [TranslateModule, DecimalPipe],
  templateUrl: './agg-feedback-view.component.html',
  styleUrls: ['./agg-feedback-view.component.scss'],
})
export class AggFeedbackViewComponent implements OnInit {
  @Input() view: FeedbackView;
  @Input() token: string;

  rounds: Round[] = [];

  private pointsOrigin: string | null = null;

  get eventUrl(): string {
    return eventPageUrl(this.view.eventId);
  }

  get resultsUrl(): string {
    return categoryResultsUrl(this.view.eventId, this.view.categoryId);
  }

  constructor(private sanitizer: DomSanitizer) {}

  ngOnInit() {
    this.applyData(this.view);
  }

  get totalPrograms(): number {
    return this.rounds.reduce((sum, round) => sum + round.programs.length, 0);
  }

  toggleProgram(program: Program) {
    program.collapsed = !program.collapsed;
  }

  toggleSection(section: Section) {
    section.collapsed = !section.collapsed;
  }

  anyExpanded(): boolean {
    return this.rounds.some((round) =>
      round.programs.some((program) => !program.collapsed || program.sections.some((section) => !section.collapsed))
    );
  }

  toggleAll() {
    const collapse = this.anyExpanded();
    this.rounds.forEach((round) =>
      round.programs.forEach((program) => {
        program.collapsed = collapse;
        program.sections.forEach((section) => (section.collapsed = collapse));
      })
    );
  }

  @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent) {
    if (!this.pointsOrigin || event.origin !== this.pointsOrigin) {
      return;
    }
    const data = event.data;
    if (!data || data.type !== 'feedback-table-height' || typeof data.height !== 'number') {
      return;
    }
    const matches = (section: Section) =>
      section.table != null &&
      String(section.table.performanceId) === String(data.performanceId) &&
      section.table.tableType === data.table;
    this.rounds.forEach((round) =>
      round.programs.forEach((program) =>
        program.sections.filter(matches).forEach((section) => (section.table!.height = Math.ceil(data.height)))
      )
    );
  }

  private originOf(url: string): string | null {
    try {
      return new URL(url).origin;
    } catch {
      return null;
    }
  }

  private applyData(view: FeedbackView) {
    this.pointsOrigin = this.originOf(view.pointsSpaBaseUrl);
    this.rounds = view.rounds.map((round) => ({
      roundName: round.roundName,
      programs: round.programs.map((program) => ({
        collapsed: false,
        sections: this.buildSections(program, view.pointsSpaBaseUrl),
      })),
    }));
  }

  private buildSections(program: FeedbackView['rounds'][number]['programs'][number], baseUrl: string): Section[] {
    const groups = this.groupBySubPanel(program.feedbacks as JudgeFeedback[]);
    const labels = new Set<string>(groups.keys());
    if (program.showTvsTable) {
      labels.add('TV');
    }
    if (program.showAvbsTable) {
      labels.add('AV-B');
    }

    return [...labels]
      .sort((a, b) => this.subPanelRank(a) - this.subPanelRank(b))
      .map((label) => {
        if (label === 'TV' && program.showTvsTable) {
          return this.tableSection('TV', this.buildTable('TVS', 'tvs-table', program.performanceId, baseUrl));
        }
        if (label === 'AV-B' && program.showAvbsTable) {
          return this.tableSection('AV-B', this.buildTable('AVBS', 'avbs-table', program.performanceId, baseUrl));
        }
        return this.judgesSection(label, groups.get(label)!);
      });
  }

  private groupBySubPanel(feedbacks: JudgeFeedback[]): Map<string, JudgeFeedback[]> {
    const groups = new Map<string, JudgeFeedback[]>();
    feedbacks.forEach((feedback) => {
      const label = this.subPanelOf(feedback.judgeCode);
      const group = groups.get(label) ?? [];
      group.push(feedback);
      groups.set(label, group);
    });
    groups.forEach((group) => group.sort((a, b) => this.judgeNumber(a.judgeCode) - this.judgeNumber(b.judgeCode)));
    return groups;
  }

  private subPanelOf(judgeCode: string): string {
    return judgeCode.replace(/\d+$/, '');
  }

  private judgeNumber(judgeCode: string): number {
    const match = judgeCode.match(/\d+$/);
    return match ? Number(match[0]) : 0;
  }

  private subPanelRank(label: string): number {
    const index = SUBPANEL_ORDER.indexOf(label);
    return index === -1 ? SUBPANEL_ORDER.length : index;
  }

  private tableSection(label: string, table: Table): Section {
    return { kind: 'table', label, collapsed: false, showScores: false, table, feedbacks: [] };
  }

  private judgesSection(label: string, feedbacks: JudgeFeedback[]): Section {
    return { kind: 'judges', label, collapsed: false, showScores: label === 'TV', table: null, feedbacks };
  }

  private buildTable(tableType: TableType, route: string, performanceId: number, baseUrl: string): Table {
    const iframeUrl = /^https?:\/\//.test(baseUrl)
      ? `${baseUrl}/${route}?performanceId=${performanceId}&token=${encodeURIComponent(this.token)}`
      : 'about:blank';
    return {
      tableType,
      performanceId,
      url: this.sanitizer.bypassSecurityTrustResourceUrl(iframeUrl),
      height: INITIAL_TABLE_HEIGHT,
    };
  }
}
